import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { SCORE_WEIGHTS, SCORE_FATOR_LABELS } from '@/pages/painel/lib/radarConstants';
import { TRIAGEM_WEIGHTS } from '@/pages/painel/lib/triagem';
import { type ConfigComercial } from '@/pages/painel/lib/comercial';
import Duplicidades from './Duplicidades';
import ImportacaoWhatsApp from './ImportacaoWhatsApp';
import RegrasManuais from './RegrasManuais';
import Conectores from './Conectores';

const TRIAGEM_LABELS: Record<string, string> = {
  geografia: 'Geografia',
  fit: 'Fit corporativo',
  recencia: 'Recência / timing',
  qualidade: 'Qualidade da informação',
  relevancia: 'Relevância estratégica',
};

export default function Configuracoes() {
  const { password } = useOutletContext<{ password: string }>();
  const [config, setConfig] = useState<ConfigComercial | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [metaMargem, setMetaMargem] = useState('');
  const [caixaMin, setCaixaMin] = useState('');
  const [caixaMax, setCaixaMax] = useState('');
  const [crescMin, setCrescMin] = useState('');
  const [crescMax, setCrescMax] = useState('');
  const [estrategicoMin, setEstrategicoMin] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'config', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { config?: ConfigComercial; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      const c = parsed?.config;
      setConfig(c ?? null);
      if (c) {
        setMetaMargem(String(c.meta_margem));
        setCaixaMin(String(c.motor_caixa_min));
        setCaixaMax(String(c.motor_caixa_max));
        setCrescMin(String(c.motor_crescimento_min));
        setCrescMax(String(c.motor_crescimento_max));
        setEstrategicoMin(String(c.projeto_estrategico_min));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar configurações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const salvar = async () => {
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: {
          action: 'config_salvar',
          password,
          meta_margem: Number(metaMargem),
          motor_caixa_min: Number(caixaMin),
          motor_caixa_max: Number(caixaMax),
          motor_crescimento_min: Number(crescMin),
          motor_crescimento_max: Number(crescMax),
          projeto_estrategico_min: Number(estrategicoMin),
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setMsg('Configurações salvas.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-xl">
          <i className="ri-settings-3-line text-lg"></i>
        </div>
        <div>
          <h2 className="font-bold text-gray-900">Configurações Comerciais</h2>
          <p className="text-sm text-gray-500">Parâmetros do Motor Comercial Integrado.</p>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
      {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{msg}</div>}

      {loading && !config ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : config ? (
        <>
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Metas e faixas comerciais</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="Meta de margem (%)" value={metaMargem} onChange={setMetaMargem} />
              <Field label="Motor Caixa — mínimo (R$)" value={caixaMin} onChange={setCaixaMin} />
              <Field label="Motor Caixa — máximo (R$)" value={caixaMax} onChange={setCaixaMax} />
              <Field label="Motor Crescimento — mínimo (R$)" value={crescMin} onChange={setCrescMin} />
              <Field label="Motor Crescimento — máximo (R$)" value={crescMax} onChange={setCrescMax} />
              <Field label="Projeto Estratégico — mínimo (R$)" value={estrategicoMin} onChange={setEstrategicoMin} />
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={salvar} disabled={saving} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-5 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Salvar
              </button>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-2">Regiões prioritárias</h3>
            <p className="text-xs text-gray-400 mb-4">Ordem de prioridade geográfica usada na triagem de obras.</p>
            <div className="space-y-1.5">
              {(config.regioes_prioritarias ?? []).map((r) => (
                <div key={r.key} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#E8610A]/10 text-[#E8610A] text-xs font-bold">{r.prioridade}</span>
                  <span className="text-gray-700">{r.label}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-2">Ajustes geográficos</h3>
            <p className="text-xs text-gray-400 mb-4">Regras explícitas de prioridade para prospecção nova. Não afetam cliente existente, oportunidade ativa, projeto real ou proposta enviada.</p>
            {(config.ajustes_geograficos ?? []).length === 0 ? (
              <p className="text-sm text-gray-400">Nenhum ajuste geográfico configurado.</p>
            ) : (
              <div className="space-y-2">
                {(config.ajustes_geograficos ?? []).map((a, i) => (
                  <div key={i} className="flex items-start gap-3 border border-gray-100 rounded-xl p-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-50 text-amber-600 rounded-lg shrink-0">
                      <i className="ri-map-pin-2-line"></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-800">{a.cidade}{a.uf ? ` / ${a.uf}` : ''}</p>
                        <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          {a.acao === 'reduzir_prospeccao_nova' ? 'Reduzir prospecção nova' : a.acao}
                        </span>
                      </div>
                      {a.motivo && <p className="text-xs text-gray-500 mt-1">{a.motivo}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Pesos — Prioridade de Triagem</h3>
              <p className="text-xs text-gray-400 mb-4">Total 100 pontos. (Somente leitura nesta versão)</p>
              <div className="space-y-2">
                {Object.entries(TRIAGEM_WEIGHTS).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="flex-1 text-sm text-gray-700">{TRIAGEM_LABELS[key] ?? key}</span>
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${val}%` }} />
                    </div>
                    <span className="w-8 text-xs font-semibold text-gray-600 text-right">{val}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Pesos — HOP Radar Score</h3>
              <p className="text-xs text-gray-400 mb-4">Total 100 pontos. (Somente leitura nesta versão)</p>
              <div className="space-y-2">
                {SCORE_WEIGHTS.map((w) => (
                  <div key={w.key} className="flex items-center gap-3">
                    <span className="flex-1 text-sm text-gray-700">{SCORE_FATOR_LABELS[w.key] ?? w.label}</span>
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#E8610A] rounded-full" style={{ width: `${w.max}%` }} />
                    </div>
                    <span className="w-8 text-xs font-semibold text-gray-600 text-right">{w.max}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </>
      ) : null}

      <Duplicidades password={password} />

      <Conectores />

      <RegrasManuais password={password} />

      <ImportacaoWhatsApp password={password} />
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-[11px] text-gray-500 font-semibold">{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
    </div>
  );
}